import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  ColumnDatas,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  Modal,
  Table,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import dayjs from "dayjs";
import useLogStore from "../../../redux/dispatcher/useLogStore";
import { LogItem } from "../../../types/files";
import utilsCommon from "../../../shared/utils/common";
import { LogDTO, SummaryLogItem } from "../../../types/log";
import DateDropdown from "../../../components/DateDropdown";
import LogDropdownFilter from "./LogDropdownFilter";
import { t } from "i18next";
import utilsDocs from "../../../shared/utils/docs";
import RevisionTag from "../../../components/RevisionTag";
import EmptyInitFilterPage from "../../../components/EmptyPage/EmptyInitFilterPage";
import EmptyFilterPage from "../../../components/EmptyPage/EmptyFilterPage";
import DocAside from "../../../components/DocAside";
import MeetingAside from "../../../components/MeetingAside";
import WorkgroupAside from "../../../components/WorkgroupAside";
import WorkgroupPolicyTooltip from "./WorkgroupPolicyTooltip";
import FilesharePolicyTooltip from "./FilesharePolicyTooltip";
import CompressWorkgroupLog from "./CompressWorkgroupLog";
import useWorkgroupStore from "../../../redux/dispatcher/useWorkgroupStore";
import { handleDocPreview } from "../../../shared/utils/api";
import UserSearchDropdown from "../../../components/UserSearchDropdown";
import { Member } from "../../../types/workgroup";
import LogSettingButton from "./LogSettingButton";
import MeetingDetailModal from "../../../components/MeetingDetailModal";
import { meetingDTO } from "../../../types/meeting";
import useMeetingStore from "../../../redux/dispatcher/useMeetingStore";
import { nanoid } from "@reduxjs/toolkit";
import currentUrlUtils from "../../../shared/utils/currentUrlUtils";
import { useNavigate } from "react-router-dom";
import { ChatLogItem } from "../../../types/chat";
import i18n from "fds/src/locales/i18n";
import { langCalendar, months } from "../WorkgroupHome";
import classNames from "classnames";

export const INIT_LOG_FILTER = {
  posPage: 1,
  pageRange: 1,
  rowRange: 30,
  startDate: undefined,
  endDate: undefined,
  filterWorkgroup: undefined, //[0, 1, 2, 3, 4],
  filterDocument: undefined, //[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  filterWebMeeting: undefined, //[0, 1, 2],
  filterChat: undefined, //[0],
  workgroupId: "",
  userId: "",
} as LogDTO;
export const FULL_FILTER_LOG_BY_TYPE = {
  filterWorkgroup: [0, 1, 2, 3, 4],
  filterDocument: [0, 7, 1, 5, 2, 9, 8, 6, 10, 4, 11, 3],
  filterWebMeeting: [0, 1, 2],
  filterChat: [0],
};
export const FULL_FILTER_LOG_BY_TYPE_RESOURCE = {
  filterWorkgroup: [
    t("log.icon.create"),
    t("log.icon.changeInfo"),
    t("log.icon.changeAuth"),
    t("log.icon.changeWorkgroupMaster"),
    t("log.icon.changeMember"),
  ],
  filterDocument: [
    t("log.icon.share"),
    t("log.icon.edit"),
    t("log.icon.comment"),
    t("log.icon.changeName"),
    t("log.icon.delete"),
    t("log.icon.download"),
    t("log.icon.unauthDownload"),
    t("log.icon.view"),
    t("log.icon.changeDocMaster"),
    t("log.icon.changeAuth"),
    t("log.icon.shareLink"),
    t("log.icon.restore"),
  ],
  filterWebMeeting: [
    t("log.icon.create"),
    t("log.icon.changeContent"),
    t("log.icon.delete"),
  ],
  filterChat: [t("log.icon.chat")],
};
/*
  워크그룹 이력 관련 로그
    HistoryType 0: 화상 회의
      saveType 0: 화상회의 예약
      saveType 1: 화상회의 내용 변경
      saveType 2: 화상회의 취소
    HistoryType 1: 문서
      saveType 0: 파일 공유
      saveType 1: 파일 편집
      saveType 2: 의견 입력
      saveType 3: 파일 이름 변경
      saveType 4: 파일 삭제
      saveType 5: 파일 다운로드
      saveType 6: 파일 암호화 해제
      saveType 7: 파일 열람
      saveType 8: 파일 정보 수정
      saveType 9: 파일 권한 변경
      saveType 10: 권한 변경
      saveType 11: 링크 설정 변경
      saveType 12: 파일 복원
    HistoryType 2: 워크그룹 
      saveType 0: 워크그룹 생성
      saveType 1: 워크그룹 정보 수정
      saveType 2: 워크그룹 권한 변경
      saveType 3: 워크그룹장 변경
      saveType 4: 워크그룹 멤버 수정
    HistoryType 3: 채팅
      saveType 0: 채팅 이력
      */
const WorkgroupLog = ({ workgroupId }: { workgroupId: string }) => {
  const navigate = useNavigate();
  const { workgroupState } = useWorkgroupStore();
  const { logState, getNewLogList, getLogDetail } = useLogStore();
  const { getMeetingDetail } = useMeetingStore();
  const toast = useToast();
  const { givenShowType } = currentUrlUtils.getAllFirstQuery();

  const dir = document.documentElement.dir; // 'ltr' 또는 'rtl'
  const logListNew = useRef([] as ColumnDatas[]);

  const [asideObject, changeAsideObject] = useState<LogItem | undefined>(
    undefined
  );
  const [compressedDateFilter, changeCompressedDateFilter] = useState({
    month: dayjs().month() + "",
    year: dayjs().year() + "",
    startMonth: dayjs().subtract(1, "month").month() + "",
    startYear: dayjs().year() + "",
    isCustomDate: false,
    customFilter: [] as string[],
  });
  const [tempCompressedDateFilter, changeTempCompressedDateFilter] = useState({
    month: undefined as string | undefined,
    year: undefined as string | undefined,
    startMonth: undefined as string | undefined,
    startYear: undefined as string | undefined,
  });
  const [openAside, toggleOpenAside] = useState(false);
  const [filter, changeFilter] = useState(INIT_LOG_FILTER);
  const [buttonClick, setButtonClick] = useState(0);
  const [dateFilter, changeDateFilter] = useState<string>("all");
  const [showType, changeShowType] = useState<"LIST" | "COMPRESS">(
    givenShowType && ["LIST", "COMPRESS"].includes(givenShowType)
      ? (givenShowType as "LIST" | "COMPRESS")
      : "COMPRESS"
  );
  const [key, setKey] = useState(0);
  const [scrollLogIds, changeScrollLogIds] = useState({
    logId: null,
    lastLogId: null,
  });
  const [docAsideIndex, changeDocAsideIndex] = useState("NONE"); // 함축이력에서 우측 상세정보 logId
  const [users, changeUsers] = useState<Member[]>([]); // 멤버 검색 필터시 사용되는 id 리스트
  const [savedUsers, changeSavedUsers] = useState<Member[]>([]); // 멤버 검색 필터시 사용되는 userID 저징
  const [settingButtonInfo, setSettingInfo] = useState<{
    x: number;
    y: number;
    logId: string;
    log?: LogItem;
    meetingInfo?: meetingDTO;
  }>({
    x: 0,
    y: 0,
    logId: "",
    log: undefined,
    meetingInfo: undefined,
  });
  const [openSelectDate, toggleOpenSelectDate] = useState(false);
  useEffect(() => {
    if (showType !== "LIST") return;
    const {
      filterChat,
      filterDocument,
      filterWebMeeting,
      filterWorkgroup,
      ...newFilter
    } = filter;
    getNewLogList(
      // filter.logId
      //   ? { ...filter, rowRange: 20, workgroupId: workgroupId }
      //   :
      {
        ...filter,
        rowRange: 20,
        workgroupId: workgroupId,
        filterWorkgroup:
          filter.filterWorkgroup ??
          (filterChat === undefined &&
          filterWebMeeting === undefined &&
          filterDocument === undefined
            ? FULL_FILTER_LOG_BY_TYPE["filterWorkgroup"]
            : []),
        filterDocument:
          filter.filterDocument ??
          (filterChat === undefined &&
          filterWorkgroup === undefined &&
          filterWebMeeting === undefined
            ? FULL_FILTER_LOG_BY_TYPE["filterDocument"]
            : []),
        filterWebMeeting:
          filter.filterWebMeeting ??
          (filterChat === undefined &&
          filterWorkgroup === undefined &&
          filterDocument === undefined
            ? FULL_FILTER_LOG_BY_TYPE["filterWebMeeting"]
            : []),
        filterChat:
          filter.filterChat ??
          (filterWebMeeting === undefined &&
          filterWorkgroup === undefined &&
          filterDocument === undefined
            ? FULL_FILTER_LOG_BY_TYPE["filterChat"]
            : []),
      }
    );
  }, [filter, showType]);
  useEffect(() => {
    let newDataContent = [] as ColumnDatas[];
    logState.newLogList?.logResults?.forEach((item: LogItem) =>
      newDataContent.push(getTableData(item))
    );
    logListNew.current = newDataContent;
    changeScrollLogIds({
      logId: logState.newLogList?.logId,
      lastLogId: logState.newLogList?.lastLogId,
    });
    setKey(key + 1);
  }, [logState.newLogList]);
  useEffect(() => {
    if (docAsideIndex === "NONE") return;
    getLogDetail({ logIdList: [docAsideIndex] }).then((resp: any) => {
      changeAsideObject(resp.payload.logResults[0]);
    });
  }, [docAsideIndex]);
  useEffect(() => {
    changeDocAsideIndex("NONE");
  }, [compressedDateFilter]);
  useEffect(() => {
    handleInitFilter();
  }, [showType]);
  const handleFocusOnLog = (row: LogItem) => {
    if (row.historyType === 0) {
    } else if (row.historyType === 1) {
    }
    changeAsideObject(row);
    toggleOpenAside(true);
  };
  const handleGetMeetingDetail = async (
    meetingId: string,
    occurrenceId?: string
  ) => {
    let resp: any = await getMeetingDetail({
      workgroupId,
      meetingId: meetingId,
      occurrenceId: occurrenceId ?? "NONE",
    });
    if (resp.payload) {
      setSettingInfo({
        ...settingButtonInfo,
        meetingInfo: resp.payload.meetingBasicInfo,
      });
    } else {
      toast.toastMsg(nanoid(), t("log.deleteMeeting"), "error");
    }
  };
  const getTableData = (row: LogItem): ColumnDatas => {
    return {
      clickAction: () => handleFocusOnLog(row),
      id: row.logId,
      clickRAction: (e) => {
        e.stopPropagation();
        row.historyType === 1 &&
          setSettingInfo({
            x: dir === "ltr" ? e.pageX - 240 : e.pageX,
            y: e.pageY - 61,
            logId: row?.logId,
            log: row,
          });
      },
      hover: row.historyType === 1 && (
        <Button
          btnStyle={"icon"}
          size="sm"
          classes="mg ml-4"
          onClick={(e) => {
            e.stopPropagation();
            setSettingInfo({
              x: dir === "ltr" ? e.pageX - 240 : e.pageX,
              y: e.pageY - 61,
              logId: row?.logId,
              log: row,
            });
          }}
          leadingIcon={<Icons variant="line" label="moreVertical" />}
        />
      ),
      data: [
        { columnText: "" },
        {
          columnText: (
            <Typography type="body2_rg">
              {utilsCommon.renderDateFormat(row.logDate)}
            </Typography>
          ),
        },
        {
          columnText: (
            <Tooltip
              message={utilsCommon.getUserNameIdTag(
                row.userId,
                row.userName,
                true
              )}
            >
              <Typography type="body2_rg" ellipsis>
                {utilsCommon.getUserNameIdTag(row.userId, row.userName, true)}
              </Typography>
            </Tooltip>
          ),
        },
        {
          columnText: renderProperDetailLog(row),
        },
        {
          columnText: row.policyLogs ? (
            <Tooltip
              toolTipMaxWidth={"max-content"}
              message={
                row.policyLogs[0].permissionPolicy.policy
                  ?.fileShareLinkPolicy &&
                (row.saveType === 10 ||
                  (row.historyType === 2 && row.saveType === 1)) ? (
                  <FilesharePolicyTooltip
                    linkPolicy={
                      row.policyLogs[0].permissionPolicy.policy
                        ?.fileShareLinkPolicy
                    }
                    logDate={row.logDate}
                  />
                ) : (
                  <WorkgroupPolicyTooltip
                    logItem={row.policyLogs}
                    title={
                      row.logType === "2"
                        ? t("workgroup.workgroupDefaultPermssion")
                        : undefined
                    }
                  />
                )
              }
            >
              <Typography type="body2_rg" ellipsis>
                {renderProperHistoryType(row)}
              </Typography>
            </Tooltip>
          ) : row.historyType === 1 && row.desc && row.saveType !== 1 ? (
            <Tooltip
              message={
                (row.saveType === 3 ? t("log.detailLog.pastFileName") : "") +
                row.desc
              }
            >
              <Typography type="body2_rg" ellipsis>
                {renderProperHistoryType(row)}
              </Typography>
            </Tooltip>
          ) : (
            <Typography type="body2_rg" ellipsis>
              {renderProperHistoryType(row)}
            </Typography>
          ),
        },
      ],
    };
  };
  const renderGetDetailStr = (row: LogItem, isTooltip: boolean) => {
    if (row.historyType === 2) {
      switch (row.saveType) {
        case 1:
          return row.policyLogs
            ? t("log.detailLog.shareLink")
            : !isTooltip
            ? row.desc ??
              (row.logDatas && row.logDatas[0]
                ? row.logDatas[0]
                : `${t("common.workgroupName")}: ` + row.workgroupName)
            : row.logDatas && row.logDatas.length > 0
            ? row.logDatas.join("\n")
            : row.desc ?? `${t("common.workgroupName")}: ` + row.workgroupName;
        case 3:
          return row.desc;
        case 4:
          return (
            (["add", "invite"].includes(row.actionType)
              ? t("log.detailLog.inviteUser")
              : t("log.detailLog.excludeUser")) + row.logDatas.join(", ")
          );
        default:
          return t("log.detailLog.setDefaultWorkgroupLog");
      }
    }
  };
  /** UI: 상세내역(문서/회의/워크그룹/채팅) 내용   */
  const renderProperDetailLog = (row: LogItem) => {
    const chat = row?.chatLogData
      ? (JSON.parse(row?.chatLogData)[0] as ChatLogItem)
      : undefined;
    return row.historyType === 0 ? (
      <Box width={"100%"}>
        <Tooltip message={row.desc} classes="hover-text-underline">
          <Typography
            type="body2_rg"
            ellipsis
            onClick={(e: any) => {
              e.stopPropagation();
              handleGetMeetingDetail(row.syncId, row.documnetName);
            }}
          >
            {row.desc}
          </Typography>
        </Tooltip>
      </Box>
    ) : row.historyType === 1 ? (
      <Box alignItems="center" width={"100%"}>
        {utilsDocs.renderDocIcon(row.documnetName, 24)}
        <Tooltip message={row.documnetName} maxWidth={"calc(100% - 75px)"}>
          <Typography
            classes={classNames("mg ml-8", {
              "hover-text-underline":
                row.syncId &&
                utilsCommon.checkLicenseAvailable("allowWebView") &&
                utilsCommon.checkExtensionAvailable(
                  row?.documnetName,
                  "fwocExtensions"
                ),
            })}
            type="body2_rg"
            ellipsis
            onClick={(e: any) => {
              e.stopPropagation();
              row.syncId &&
                utilsCommon.checkLicenseAvailable("allowWebView") &&
                utilsCommon.checkExtensionAvailable(
                  row?.documnetName,
                  "fwocExtensions"
                ) &&
                handleDocPreview({
                  permission: "view",
                  workgroupId: workgroupId,
                  syncId: row.syncId,
                  toast,
                  t,
                  revisionNo: row?.revisionNo,
                });
            }}
          >
            {row.documnetName}
          </Typography>
        </Tooltip>
        <RevisionTag revisionNo={row.revisionNo} classes="mg ml-8" />
      </Box>
    ) : row.historyType === 3 ? (
      <Box
        onClick={() =>
          chat?.messageId &&
          navigate(
            `${process.env.PUBLIC_URL}/workgroup/${row.workgroupId}/chat?chatId=${chat?.messageId}`
          )
        }
      >
        {chat?.parentMessage && (
          <Typography ellipsis type="body2_rg" classes="mg mr-4">
            {"Re: "}
          </Typography>
        )}

        {chat?.body ? (
          <Tooltip message={chat?.body} maxWidth={"calc(100% - 40px)"}>
            <Typography ellipsis type="body2_rg">
              {chat?.body}
            </Typography>
          </Tooltip>
        ) : (
          <Box alignItems="center" width={"100%"}>
            {utilsDocs.renderDocIcon(chat?.attachments[0]?.title, 24)}
            <Tooltip
              message={chat?.attachments[0]?.title}
              maxWidth={"calc(100% - 75px)"}
            >
              <Typography classes="mg ml-8" type="body2_rg" ellipsis>
                {chat?.attachments[0]?.title}
              </Typography>
            </Tooltip>
          </Box>
        )}
      </Box>
    ) : (
      <Box width={"100%"}>
        <Tooltip message={renderGetDetailStr(row, true)}>
          <Typography type="body2_rg" ellipsis>
            {renderGetDetailStr(row, false)}
          </Typography>
        </Tooltip>
      </Box>
    );
  };
  const renderProperHistoryType = (row: LogItem) => {
    return row.historyType === 0
      ? t(`log.filterWebMeeting.${row.saveType}`)
      : row.historyType === 1
      ? t(`log.filterDocument.${row.saveType}`)
      : row.historyType === 2
      ? t(`log.filterWorkgroup.${row.saveType}`)
      : t(`log.filterChat.${row.saveType}`);
  };
  /** 필터 변경 */
  const changeFilterByKey = (key: string, value: any) => {
    let newValue = value;

    if (key === "date") {
      toggleOpenAside(false);
      if (typeof value !== "object") {
        changeDateFilter(value);
        changeFilter({
          ...filter,
          logId: undefined,
          startDate: utilsCommon.getStartPeriod(value),
          endDate: dayjs().valueOf(),
        });
      } else {
        changeDateFilter("custom");
        changeFilter({
          ...filter,
          logId: undefined,
          startDate: dayjs(value?.start ?? undefined).valueOf(),
          endDate: dayjs(value?.end ?? undefined).valueOf(),
        });
      }
      document.getElementById("docTable")?.scrollTo(0, 0);
      changeScrollLogIds({
        logId: null,
        lastLogId: null,
      });
      return;
    } else if (key === "logId") {
      changeFilter({ ...filter, logId: newValue });
      return;
    } else if (key === "userId") {
      changeSavedUsers(users);
      changeFilter({ ...filter, logId: undefined, userId: newValue[0] });
      toggleOpenAside(false);
      changeScrollLogIds({
        logId: null,
        lastLogId: null,
      });
      document.getElementById("docTable")?.scrollTo(0, 0);
      return;
    }
    toggleOpenAside(false);
    changeScrollLogIds({
      logId: null,
      lastLogId: null,
    });
    document.getElementById("docTable")?.scrollTo(0, 0);
    changeFilter({ ...filter, logId: undefined, [key]: newValue });
  };
  const handleInitFilter = () => {
    if (showType === "LIST") {
      changeFilter(INIT_LOG_FILTER);
      changeDateFilter("all");
      setButtonClick(buttonClick + 1);
    } else {
      changeCompressedDateFilter({
        month: dayjs().month() + "",
        year: dayjs().year() + "",
        startMonth: dayjs().subtract(1, "month").month() + "",
        startYear: dayjs().year() + "",
        isCustomDate: false,
        customFilter: [] as string[],
      });
    }
    changeDocAsideIndex("NONE");
    changeAsideObject(undefined);
    document.getElementById("docTable")?.scrollTo(0, 0);
  };
  /** UI : 이력  상단 필터 */
  const renderFilter = () => {
    return showType === "LIST" ? (
      <Box
        alignItems="center"
        minHeight={72}
        classes="workgroupLog-filters  mg mb-20"
      >
        <Button
          leadingIcon={<Icons variant="line" label="redoLeft" />}
          btnStyle="icon"
          size={"sm"}
          onClick={handleInitFilter}
          state={
            JSON.stringify({ ...filter, logId: "" }) ===
            JSON.stringify({ ...INIT_LOG_FILTER, logId: "" })
              ? "disable"
              : "default"
          }
        />
        <DateDropdown
          filter={filter}
          buttonClick={buttonClick}
          dateFilter={dateFilter}
          setButtonClick={setButtonClick}
          initValue={"all"}
          changeFilterByKey={changeFilterByKey}
        />
        <LogDropdownFilter
          buttonClick={buttonClick}
          filter={filter}
          type={"filterDocument"}
          changeFilterByKey={changeFilterByKey}
          setButtonClick={setButtonClick}
        />
        <UserSearchDropdown
          buttonClick={buttonClick}
          filter={filter}
          users={users}
          savedUsers={savedUsers}
          isSingle
          workgroupId={workgroupId}
          pageFrom="workgroupLog"
          isAllowNewUser={false}
          changeUsers={changeUsers}
          changeFilterByKey={changeFilterByKey}
          setButtonClick={setButtonClick}
          buttonTitle={t("common.user")}
        />
        <LogDropdownFilter
          buttonClick={buttonClick}
          filter={filter}
          type={"filterWorkgroup"}
          changeFilterByKey={changeFilterByKey}
          setButtonClick={setButtonClick}
        />
        {utilsCommon.checkMenuAvailable("zoom") !== false && (
          <LogDropdownFilter
            buttonClick={buttonClick}
            filter={filter}
            type={"filterWebMeeting"}
            changeFilterByKey={changeFilterByKey}
            setButtonClick={setButtonClick}
          />
        )}
        {utilsCommon.getChatAvailibility() === "true" && (
          <LogDropdownFilter
            buttonClick={buttonClick}
            filter={filter}
            type={"filterChat"}
            changeFilterByKey={changeFilterByKey}
            setButtonClick={setButtonClick}
          />
        )}
        {renderChangeShowType()}
      </Box>
    ) : (
      <Box
        alignItems="center"
        minHeight={72}
        classes="workgroupLog-filters  mg mb-12"
        width={"100%"}
      >
        <Button
          leadingIcon={<Icons variant="line" label="redoLeft" />}
          btnStyle="icon"
          size={"sm"}
          onClick={handleInitFilter}
          state={
            JSON.stringify(compressedDateFilter) ===
            JSON.stringify({
              month: dayjs().month() + "",
              year: dayjs().year() + "",
              startMonth: dayjs().subtract(1, "month").month() + "",
              startYear: dayjs().year() + "",
              isCustomDate: false,
              customFilter: [] as string[],
            })
              ? "disable"
              : "default"
          }
        />
        {renderCompressedCalendarFilter()}
        {renderChangeShowType()}
      </Box>
    );
  };
  const [tempYear, changeTempyear] = useState(
    parseInt(compressedDateFilter.year)
  );
  const renderCompressedCalendarFilter = () => {
    const filters = ["file", "workgroup"];
    utilsCommon.checkMenuAvailable("zoom") !== false && filters.push("meeting");
    utilsCommon.getChatAvailibility() !== "noDisplay" && filters.push("chat");
    return (
      <Box alignItems="center">
        <Dropdown
          withAction={() => changeTempyear(parseInt(compressedDateFilter.year))}
          placeholder={t("common.period.title")}
          value={
            compressedDateFilter.startYear === dayjs().get("year") + "" &&
            compressedDateFilter.startMonth ===
              dayjs().subtract(1, "month").get("month") + ""
              ? undefined
              : dayjs().diff(
                  dayjs()
                    .set("year", parseInt(compressedDateFilter.startYear))
                    .set("month", parseInt(compressedDateFilter.startMonth)),
                  "month"
                ) <= 3 &&
                compressedDateFilter.month === dayjs().month() + "" &&
                compressedDateFilter.year === dayjs().year() + "" &&
                !compressedDateFilter.isCustomDate
              ? dayjs().diff(
                  dayjs()
                    .set("month", parseInt(compressedDateFilter.startMonth))
                    .valueOf(),
                  "month"
                ) === 1
                ? t("common.period.lastMonth")
                : t("common.period.lastNMonth", {
                    0: dayjs().diff(
                      dayjs()
                        .set("month", parseInt(compressedDateFilter.startMonth))
                        .valueOf(),
                      "month"
                    ),
                  })
              : `${utilsCommon.renderDateFormat(
                  dayjs()
                    .set("year", parseInt(compressedDateFilter.startYear))
                    .set("month", parseInt(compressedDateFilter.startMonth))
                    .valueOf(),
                  t("common.monthAndYear")
                )} ~ ${utilsCommon.renderDateFormat(
                  dayjs()
                    .set("year", parseInt(compressedDateFilter.year))
                    .set("month", parseInt(compressedDateFilter.month))
                    .valueOf(),
                  t("common.monthAndYear")
                )}`
          }
          showValue
          buttonClick={buttonClick}
          buttonWidth={240}
          dropdownMaxHeight={396}
          btnStyle="round"
          btnSize="rd"
        >
          <Box direction="column" maxHeight={234} style={{ overflow: "auto" }}>
            {Array(3)
              .fill("")
              .map((item, index) => {
                const currDate = dayjs()
                  .subtract(index + 1, "month")
                  .valueOf();
                const checked =
                  compressedDateFilter.startYear ===
                    dayjs(currDate).get("year") + "" &&
                  compressedDateFilter.startMonth ===
                    dayjs(currDate).get("month") + "" &&
                  !compressedDateFilter.isCustomDate;
                return (
                  <DropdownItem
                    label={
                      index === 0
                        ? t("common.period.lastMonth")
                        : t("common.period.lastNMonth", {
                            0: index + 1,
                          })
                    }
                    checked={checked}
                    checkColor={checked}
                    onClick={() => {
                      setButtonClick(buttonClick + 1);
                      changeCompressedDateFilter({
                        ...compressedDateFilter,
                        startMonth: dayjs(currDate).get("month") + "",
                        startYear: dayjs(currDate).get("year") + "",
                        month: dayjs().get("month") + "",
                        year: dayjs().get("year") + "",
                        isCustomDate: false,
                      });
                    }}
                    type="check"
                  />
                );
              })}
          </Box>

          <DropdownItem state="divider" />
          <DropdownItem
            label={t("common.period.userRange")}
            type="check"
            onClick={() => toggleOpenSelectDate(true)}
          />
        </Dropdown>
        {filters.map((item) => (
          <Button
            label={t("common." + item)}
            classes="mg ml-12 workgroupLog-filters--docOnly"
            state={
              compressedDateFilter.customFilter.includes(item)
                ? "select"
                : "default"
            }
            size="rd"
            onClick={() =>
              changeCompressedDateFilter({
                ...compressedDateFilter,
                customFilter: compressedDateFilter.customFilter.includes(item)
                  ? compressedDateFilter.customFilter.filter(
                      (item1) => item1 !== item
                    )
                  : [...compressedDateFilter.customFilter, item],
              })
            }
          />
        ))}
      </Box>
    );
  };
  const renderLogDetail = () => {
    const type = asideObject?.historyType;

    return type === 0 ? (
      <MeetingAside
        asideId={workgroupId + "@@" + asideObject?.syncId}
        meetingName={asideObject?.desc ?? ""}
      />
    ) : type === 1 ? (
      <DocAside
        page={"log"}
        asideInfo={{
          syncId: asideObject?.syncId + "",
          revisionNo: asideObject?.revisionNo ?? 0,
          workgroupId: workgroupId,
          contentName: "",
        }}
      />
    ) : type === 2 ? (
      <WorkgroupAside workgroupId={workgroupId} />
    ) : (
      <Box classes={"docAside"} alignItems="center">
        <Box height={160} alignItems="center" justifyContent="center">
          <img
            src={require("../../../shared/images/pages/File/EmptyDetail.png")}
            alt={"thumbnail"}
            width={140}
            height={110}
          />
        </Box>
        <Typography
          type="body2_rg"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("log.selectLog")}
        </Typography>
      </Box>
    );
  };
  const renderTableList = () => {
    return (
      <Box
        width={"100%"}
        height={showType === "LIST" ? "calc(100% - 75px)" : "100%"}
        style={{ overflow: "auto" }}
      >
        {showType === "LIST" ? (
          <Box direction="column" width={"100%"} classes="workgroupLog">
            <Table
              header={[
                {
                  headerText: "",
                  width: "16px",
                },
                {
                  headerText: t("common.time"),
                  width: "204px",
                },
                {
                  headerText: t("common.user"),
                  width: "160px",
                },
                {
                  headerText: t("log.target"),
                  width: "calc((100% - 380px) * 2 / 3)",
                },
                {
                  headerText: t("common.type"),
                  width: "calc((100% - 380px) / 3)",
                },
              ]}
              asideId={asideObject?.logId}
              columnHeight={"md"}
              sideMenu={!openAside ? "on" : "off"}
              targetId={settingButtonInfo.logId}
              sideMenuAction={() => toggleOpenAside(!openAside)}
              width={"100%"}
              data={logListNew.current}
              height={
                logListNew.current?.length === 0
                  ? undefined
                  : "calc(100vh - 351px)"
              }
              endReached={() =>
                scrollLogIds.lastLogId &&
                scrollLogIds.lastLogId !== scrollLogIds.logId &&
                logState.newLogList?.total > logListNew.current.length &&
                changeFilterByKey("logId", scrollLogIds.lastLogId)
              }
            />
            {logListNew.current?.length === 0 &&
              (JSON.stringify(filter) !== JSON.stringify(INIT_LOG_FILTER) ? (
                <EmptyFilterPage
                  pageType={"log"}
                  handleInitFilter={handleInitFilter}
                />
              ) : (
                <EmptyInitFilterPage pageType={"log"} />
              ))}
          </Box>
        ) : (
          <CompressWorkgroupLog
            workgroupId={workgroupId}
            pageType="workgroupLog"
            dateFilter={compressedDateFilter}
            docAsideIndex={docAsideIndex}
            changeDocAsideIndex={changeDocAsideIndex}
            settingButtonInfo={settingButtonInfo}
            setSettingInfo={setSettingInfo}
            handleGetMeetingDetail={handleGetMeetingDetail}
            handleInitFilter={handleInitFilter}
          />
        )}
        {(openAside || docAsideIndex !== "NONE") && renderLogDetail()}
      </Box>
    );
  };
  const getDropdownMonthCalc = (workgroupCreateDate: {
    year: number;
    month: number;
    isStart: boolean;
  }) => {
    const now = dayjs();
    const currentYear = now.year();
    const currentMonth = now.month(); // 1~12
    const createdYear = workgroupCreateDate.year;
    const createdMonth = workgroupCreateDate.month; // 1~12
    const selectedYear = Number(
      workgroupCreateDate.isStart
        ? tempCompressedDateFilter.startYear
        : tempCompressedDateFilter.year
    );

    let startMonth = 0;
    let endMonth = 11;

    if (selectedYear === currentYear) {
      // 올해
      if (createdYear === currentYear) {
        startMonth = createdMonth;
      }
      endMonth = currentMonth;
    } else if (selectedYear === createdYear) {
      // 생성년도
      startMonth = createdMonth;
      endMonth = 11;
    }
    // 과거연도는 startMonth=1, endMonth=12 그대로

    return endMonth - startMonth + 1;
  };
  const getValidMonthFromYear = (
    workgroupCreateDate: {
      year: number;
      month: number;
    },
    isStart: boolean
  ) => {
    const now = dayjs();
    const currentYear = now.year();
    const currentMonth = now.month(); // 1~12
    const createdYear = workgroupCreateDate.year;
    const createdMonth = workgroupCreateDate.month + 1; // 1~12
    const selectedYear = Number(
      isStart
        ? tempCompressedDateFilter.startYear
        : tempCompressedDateFilter.year
    );
    const selectedMonth = Number(
      isStart
        ? tempCompressedDateFilter.startMonth
        : tempCompressedDateFilter.month
    );

    // case 1: 올해 생성했고 선택한 연도가 현재달보다 클경우 => 현재달로 수정
    if (
      createdYear === currentYear &&
      (selectedMonth > currentMonth || selectedMonth < createdMonth)
    ) {
      return currentMonth;
    }
    // case 2: 선택한 연도와 생성연도가 같고 선택달이 생성달보다 작을 경우 => 생성달로 수정
    if (createdYear === selectedYear && selectedMonth < createdMonth) {
      return createdMonth;
    }
    return selectedMonth;
  };
  const renderChooseDateModal = () => {
    const logDate = workgroupState?.workgroupDetail?.workgroup?.logDate;
    const workgroupCreateDate = {
      year: dayjs(logDate).get("year"),
      month: dayjs(logDate).get("month"),
    };
    return (
      <Modal
        open={true}
        onClose={() => toggleOpenSelectDate(false)}
        width={i18n.language.includes("ko") ? 562 : 502}
        title={t("common.period.userRange")}
        isCustom
      >
        <Box height={200} classes="compressed-dateCustomFilter">
          <Box classes="compressed-dateCustomFilter--header">
            <Typography type="subtitle1">
              {t("common.period.userRange")}
            </Typography>
            <Button
              classes="mg ml-auto"
              btnStyle="icon"
              onClick={() => toggleOpenSelectDate(false)}
              leadingIcon={<Icons variant="line" label="closeLarge" />}
            />
          </Box>
          <Box
            alignItems="center"
            classes="compressed-dateCustomFilter--body"
            justifyContent="space-between"
          >
            <Box
              direction={i18n.language.includes("ko") ? "row" : "row-reverse"}
              alignItems="center"
            >
              <Box alignItems="center">
                <Dropdown
                  placeholder={dayjs().get("year") + ""}
                  value={tempCompressedDateFilter?.startYear}
                  width={100}
                  positionStrategy="fixed"
                  buttonClick={buttonClick}
                  dropdownMaxHeight={176}
                >
                  {Array(dayjs().diff(logDate, "year") + 1)
                    .fill("")
                    .map((item, index) => (
                      <DropdownItem
                        onClick={() => {
                          changeTempCompressedDateFilter({
                            ...tempCompressedDateFilter,
                            startMonth: tempCompressedDateFilter.startMonth
                              ? getValidMonthFromYear(
                                  workgroupCreateDate,
                                  true
                                ) + ""
                              : undefined,
                            startYear: dayjs()
                              .subtract(index, "year")
                              .format("YYYY"),
                          });
                          setButtonClick(buttonClick + 1);
                        }}
                        label={dayjs().subtract(index, "year").format("YYYY")}
                      />
                    ))}
                </Dropdown>
                {i18n.language.includes("ko") && (
                  <Typography type="body2_rg" classes="mg ml-6">
                    년
                  </Typography>
                )}
              </Box>
              <Box
                alignItems="center"
                classes={classNames({
                  "mg ml-8": i18n.language.includes("ko"),
                  "mg mr-8": !i18n.language.includes("ko"),
                })}
              >
                <Dropdown
                  placeholder={
                    i18n.language.includes("ko")
                      ? dayjs().get("month") + 1 + ""
                      : months[langCalendar][dayjs().get("month")]
                  }
                  value={
                    !tempCompressedDateFilter?.startMonth
                      ? undefined
                      : i18n.language.includes("ko")
                      ? Number(tempCompressedDateFilter?.startMonth) + 1 + ""
                      : months[langCalendar][
                          Number(tempCompressedDateFilter?.startMonth)
                        ]
                  }
                  width={100}
                  positionStrategy="fixed"
                  buttonClick={buttonClick}
                  dropdownMaxHeight={176}
                >
                  {Array(
                    getDropdownMonthCalc({
                      ...workgroupCreateDate,
                      isStart: true,
                    })
                  )
                    .fill("")
                    .map((item, index) => {
                      const month =
                        workgroupCreateDate.year + "" ===
                        tempCompressedDateFilter.startYear
                          ? workgroupCreateDate.month + index
                          : index;
                      return (
                        <DropdownItem
                          onClick={() => {
                            changeTempCompressedDateFilter({
                              ...tempCompressedDateFilter,
                              startMonth: month + "",
                            });
                            setButtonClick(buttonClick + 1);
                          }}
                          label={
                            i18n.language.includes("ko")
                              ? month + 1 + ""
                              : months[langCalendar][month]
                          }
                        />
                      );
                    })}
                </Dropdown>
                {i18n.language.includes("ko") && (
                  <Typography type="body2_rg" classes="mg ml-6">
                    월
                  </Typography>
                )}
              </Box>
            </Box>
            ~
            <Box
              direction={i18n.language.includes("ko") ? "row" : "row-reverse"}
              alignItems="center"
            >
              <Box alignItems="center">
                <Dropdown
                  placeholder={dayjs().get("year") + ""}
                  value={tempCompressedDateFilter?.year}
                  width={100}
                  positionStrategy="fixed"
                  buttonClick={buttonClick}
                  dropdownMaxHeight={176}
                >
                  {Array(dayjs().diff(logDate, "year") + 1)
                    .fill("")
                    .map((item, index) => (
                      <DropdownItem
                        onClick={() => {
                          changeTempCompressedDateFilter({
                            ...tempCompressedDateFilter,
                            month: tempCompressedDateFilter.month
                              ? getValidMonthFromYear(
                                  workgroupCreateDate,
                                  false
                                ) + ""
                              : undefined,
                            year: dayjs()
                              .subtract(index, "year")
                              .format("YYYY"),
                          });
                          setButtonClick(buttonClick + 1);
                        }}
                        label={dayjs().subtract(index, "year").format("YYYY")}
                      />
                    ))}
                </Dropdown>
                {i18n.language.includes("ko") && (
                  <Typography type="body2_rg" classes="mg ml-6">
                    년
                  </Typography>
                )}
              </Box>
              <Box
                alignItems="center"
                classes={classNames({
                  "mg ml-8": i18n.language.includes("ko"),
                  "mg mr-8": !i18n.language.includes("ko"),
                })}
              >
                <Dropdown
                  placeholder={
                    i18n.language.includes("ko")
                      ? dayjs().get("month") + 1 + ""
                      : months[langCalendar][dayjs().get("month")]
                  }
                  value={
                    !tempCompressedDateFilter?.month
                      ? undefined
                      : i18n.language.includes("ko")
                      ? Number(tempCompressedDateFilter?.month) + 1 + ""
                      : months[langCalendar][
                          Number(tempCompressedDateFilter?.month)
                        ]
                  }
                  width={100}
                  positionStrategy="fixed"
                  buttonClick={buttonClick}
                  dropdownMaxHeight={176}
                >
                  {Array(
                    getDropdownMonthCalc({
                      ...workgroupCreateDate,
                      isStart: false,
                    })
                  )
                    .fill("")
                    .map((item, index) => {
                      const month =
                        workgroupCreateDate.year + "" ===
                        tempCompressedDateFilter.year
                          ? workgroupCreateDate.month + index
                          : index;
                      return (
                        <DropdownItem
                          onClick={() => {
                            changeTempCompressedDateFilter({
                              ...tempCompressedDateFilter,
                              month: month + "",
                            });
                            setButtonClick(buttonClick + 1);
                          }}
                          label={
                            i18n.language.includes("ko")
                              ? month + 1 + ""
                              : months[langCalendar][month]
                          }
                        />
                      );
                    })}
                </Dropdown>
                {i18n.language.includes("ko") && (
                  <Typography type="body2_rg" classes="mg ml-6">
                    월
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <Typography
            type="body4_rg"
            classes="mg ml-auto mr-24"
            exactColor={getColor("COLOR_TEXT_BRAND")}
          >
            {t("common.period.selectPeriodNoti")}
          </Typography>
          <Box classes="compressed-dateCustomFilter--footer">
            <Button
              label={t("common.cancel")}
              btnStyle="fill"
              type="secondary"
              onClick={() => {
                toggleOpenSelectDate(false);
                changeTempCompressedDateFilter({
                  month: undefined,
                  year: undefined,
                  startMonth: undefined,
                  startYear: undefined,
                });
              }}
            />
            <Button
              label={t("common.apply")}
              btnStyle="fill"
              onClick={() => {
                toggleOpenSelectDate(false);
                if (
                  !!tempCompressedDateFilter.month &&
                  !!tempCompressedDateFilter.year &&
                  !!tempCompressedDateFilter.startMonth &&
                  !!tempCompressedDateFilter.startYear
                ) {
                  changeCompressedDateFilter({
                    ...compressedDateFilter,
                    startMonth:
                      parseInt(tempCompressedDateFilter.startMonth) + "",
                    startYear: tempCompressedDateFilter.startYear,
                    month: parseInt(tempCompressedDateFilter.month) + "",
                    year: tempCompressedDateFilter.year,
                    isCustomDate: true,
                  });
                }
                changeTempCompressedDateFilter({
                  startMonth: undefined,
                  startYear: undefined,
                  month: undefined,
                  year: undefined,
                });
              }}
              state={
                tempCompressedDateFilter.month === undefined ||
                tempCompressedDateFilter.year === undefined ||
                tempCompressedDateFilter.startMonth === undefined ||
                tempCompressedDateFilter.startYear === undefined ||
                ![0, 1, 2].includes(
                  dayjs()
                    .set("month", parseInt(tempCompressedDateFilter.month))
                    .set("year", parseInt(tempCompressedDateFilter.year))
                    .diff(
                      dayjs()
                        .set(
                          "month",
                          parseInt(tempCompressedDateFilter.startMonth)
                        )
                        .set(
                          "year",
                          parseInt(tempCompressedDateFilter.startYear)
                        ),
                      "month"
                    )
                )
                  ? "disable"
                  : "default"
              }
            />
          </Box>
        </Box>
      </Modal>
    );
  };
  const renderChangeShowType = () => {
    return (
      <Dropdown
        placeholder={t("log.listType")}
        btnStyle="link"
        value={showType === "LIST" ? t("log.list") : t("log.compress")}
        buttonWidth={204}
        leadingIcon={
          <Typography
            type="body2_rg"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {t("log.listType")}
          </Typography>
        }
        align="right"
        buttonClick={buttonClick}
        classes="mg ml-auto"
      >
        <DropdownItem
          label={t("log.compress")}
          checkColor={showType === "COMPRESS"}
          checked={showType === "COMPRESS"}
          type="check"
          onClick={() => {
            changeShowType("COMPRESS");
            setButtonClick(buttonClick + 1);
          }}
        />
        <DropdownItem
          label={t("log.list")}
          checkColor={showType === "LIST"}
          checked={showType === "LIST"}
          type="check"
          onClick={() => {
            changeShowType("LIST");
            setButtonClick(buttonClick + 1);
          }}
        />
      </Dropdown>
    );
  };
  return (
    <Box width={"100%"} height={"100%"} classes={"body"} direction="column">
      {openSelectDate && renderChooseDateModal()}
      {renderFilter()}
      {renderTableList()}
      {settingButtonInfo.logId && (
        <LogSettingButton
          settingButtonInfo={settingButtonInfo}
          setSettingInfo={setSettingInfo}
        />
      )}
      {settingButtonInfo.meetingInfo && (
        <MeetingDetailModal
          open={!!settingButtonInfo.meetingInfo}
          onClose={() => {
            setSettingInfo({
              x: 0,
              y: 0,
              logId: "",
              log: undefined,
            });
          }}
          meetingInfo={settingButtonInfo.meetingInfo}
        />
      )}
    </Box>
  );
};

export default WorkgroupLog;
